import { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Edit2, Eye, Loader2, Undo2, X, XCircle } from 'lucide-react';
import { lettersApi } from '../../services/api';
import { LetterDocument } from './LetterDocument';
import { NewLetterEditor } from './new-editor/NewLetterEditor';
import type { PdfDownloaderFn } from './new-editor/components/PreviewPane';
import { extractSignaturePlacementFromBody } from './signatureLayoutShared';
import { formatISTDateTime } from '../../utils/dateTime';

// Format ISO timestamp to dd-mm-yyyy hh:mm format
const formatDateTime = (dateString: string | null | undefined): string => {
  return formatISTDateTime(dateString);
};

// Off-screen host for the editor-accurate PDF renderer. Must use REAL layout
// (not display:none) so letterhead/footer images load and the editor can
// measure page count / header-footer heights; positioned far off-screen and
// non-interactive so the reviewer never sees it.
const OFFSCREEN_EDITOR_HOST_STYLE: CSSProperties = {
  position: 'fixed',
  left: '-99999px',
  top: 0,
  // Match the live editor's real width (CreateLetterPage renders it wide) so the
  // preview pane is wider than the letter page and PreviewPane measures the
  // letterhead/footer/page sizes identically to the on-screen editor.
  width: '1800px',
  height: '1400px',
  opacity: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: -1,
};

// Wait until a PreviewPane has registered its exporter, then allow a short
// settle so letterhead/footer images load and pagination commits before we
// serialise the document to a Blob.
const waitForPdfDownloader = async (
  ref: MutableRefObject<PdfDownloaderFn | null>,
  settleMs = 900,
): Promise<PdfDownloaderFn> => {
  const deadline = Date.now() + 5000;
  while (!ref.current) {
    if (Date.now() > deadline) {
      throw new Error('Editor renderer did not initialise in time.');
    }
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
  }
  await new Promise((resolve) => setTimeout(resolve, settleMs));
  if (!ref.current) {
    throw new Error('Editor renderer was torn down before export.');
  }
  return ref.current;
};

const noopEditorChange = () => {};

type ApprovalFilterKey = 'pending_with_you' | 'requested_by_you';

interface PendingLetterSummary {
  id: number;
  serial_number: number;
  letter_number?: string | null;
  subject: string;
  file_name?: string;
  letter_date: string;
  department?: string;
  status: 'pending_approval' | 'approved' | 'rejected';
  assigned_signatory_user_id?: number | null;
  assigned_signatory_name?: string;
  assigned_signatory_email?: string;
  created_by_name: string;
  created_by_email: string;
  updated_at: string;
  rejection_remark?: string | null;
}

interface PendingLetterDetail extends PendingLetterSummary {
  body_html: string;
  letterhead_html: string;
  footer_html: string;
  approved_signature_html?: string | null;
  qr_code_data?: string | null;
}

interface PendingApprovalResponse {
  filter: ApprovalFilterKey;
  hasValidSignature?: boolean;
  counts: {
    pending_with_you: number;
    requested_by_you: number;
  };
  letters: PendingLetterSummary[];
}

export function PendingApprovalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilterParam = searchParams.get('filter');
  const initialFilter: ApprovalFilterKey =
    initialFilterParam === 'requested_by_you' || initialFilterParam === 'pending_with_you'
      ? initialFilterParam
      : 'pending_with_you';

  const [letters, setLetters] = useState<PendingLetterSummary[]>([]);
  const [activeFilter, setActiveFilter] = useState<ApprovalFilterKey>(initialFilter);
  const [counts, setCounts] = useState({
    pending_with_you: 0,
    requested_by_you: 0,
  });
  const [hasValidSignature, setHasValidSignature] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<PendingLetterDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [accessPopupMessage, setAccessPopupMessage] = useState('');
  const [approveTarget, setApproveTarget] = useState<PendingLetterSummary | PendingLetterDetail | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingLetterSummary | PendingLetterDetail | null>(null);
  const [rejectRemark, setRejectRemark] = useState('');
  const [rejectRemarkError, setRejectRemarkError] = useState('');
  const [pendingOpenLetterId, setPendingOpenLetterId] = useState<number | null>(null);
  const latestLoadRequestRef = useRef(0);

  // Editor-accurate preview: render the selected letter through the SAME engine
  // the draft editor uses and show the resulting PDF in an iframe, so the
  // reviewer sees exactly what the final letter looks like (correct tables and
  // signature placement) instead of the divergent LetterDocument render.
  const [previewRenderData, setPreviewRenderData] = useState<PendingLetterDetail | null>(null);
  const previewPdfDownloaderRef = useRef<PdfDownloaderFn | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');
  const [previewPdfFailed, setPreviewPdfFailed] = useState(false);
  const previewRenderExtract = useMemo(
    () => (previewRenderData ? extractSignaturePlacementFromBody(previewRenderData.body_html) : null),
    [previewRenderData],
  );

  // When a letter is opened for review, render it via the editor engine and
  // show the resulting PDF in an iframe (identical to the eventual download).
  // Falls back to <LetterDocument> on any error so the preview never blanks.
  useEffect(() => {
    if (!selectedLetter?.id) {
      setPreviewRenderData(null);
      setPreviewPdfFailed(false);
      setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });
      return;
    }

    let cancelled = false;
    let createdUrl = '';

    setPreviewPdfFailed(false);
    setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });

    // PDF-upload letters are pre-paginated full-page images (.sepl-pdf-page).
    // The editor engine re-paginates them and can inject blank leading/trailing
    // pages, so route these directly to the LetterDocument fallback renderer.
    if (/sepl-pdf-page/i.test(selectedLetter.body_html || '')) {
      setPreviewPdfFailed(true);
      setPreviewRenderData(null);
      return;
    }

    setPreviewRenderData(selectedLetter);

    (async () => {
      try {
        const downloader = await waitForPdfDownloader(previewPdfDownloaderRef);
        const blob = await downloader('blob');
        if (cancelled) return;
        if (!(blob instanceof Blob) || blob.size <= 0) {
          throw new Error('Editor preview returned an empty PDF.');
        }
        createdUrl = URL.createObjectURL(blob);
        setPreviewPdfUrl(createdUrl);
      } catch (previewErr) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('Editor-accurate preview failed; showing fallback.', previewErr);
        setPreviewPdfFailed(true);
      } finally {
        if (!cancelled) {
          // Unmount the heavy off-screen editor; the blob URL keeps the preview.
          setPreviewRenderData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [selectedLetter]);

  const navigateTo = (page: string, extra: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    params.set('page', page);
    Object.entries(extra).forEach(([key, value]) => params.set(key, value));
    setSearchParams(params);
  };

  const filterCards = useMemo(() => ([
    {
      key: 'pending_with_you' as ApprovalFilterKey,
      title: 'Approval Pending with You',
      count: counts.pending_with_you,
      tone: 'from-violet-500 to-indigo-600',
    },
    {
      key: 'requested_by_you' as ApprovalFilterKey,
      title: 'Approval Requested by You',
      count: counts.requested_by_you,
      tone: 'from-sky-500 to-blue-600',
    },
  ]), [counts]);

  const tableLetters = useMemo(
    () => [...letters].sort((a, b) => {
      const score = (status: PendingLetterSummary['status']) => (status === 'pending_approval' ? 0 : status === 'rejected' ? 1 : 2);
      const statusDiff = score(a.status) - score(b.status);
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }),
    [letters],
  );

  const closeModals = () => {
    setApproveTarget(null);
    setRejectTarget(null);
    setRejectRemark('');
    setRejectRemarkError('');
  };

  const openPreview = useCallback(async (letterId: number) => {
    try {
      setDetailLoading(true);
      setAccessPopupMessage('');
      const response = await lettersApi.get(letterId);
      setSelectedLetter(response.data);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load letter.';
      if (err?.response?.status === 403) {
        setSelectedLetter(null);
        setAccessPopupMessage(message);
      } else {
        setError(message);
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadLetters = useCallback(async (
    filter: ApprovalFilterKey,
    preferredLetterId?: number | null,
    options?: { silent?: boolean; signal?: AbortSignal },
  ) => {
    const requestId = ++latestLoadRequestRef.current;
    try {
      if (!options?.silent) {
        setLoading(true);
        setError('');
      }
      const response = await lettersApi.listPendingApproval({ filter }, { signal: options?.signal });

      const payload: PendingApprovalResponse = Array.isArray(response.data)
        ? {
            filter,
            hasValidSignature: true,
            counts: {
              pending_with_you: filter === 'pending_with_you' ? response.data.length : 0,
              requested_by_you: filter === 'requested_by_you' ? response.data.length : 0,
            },
            letters: response.data,
          }
        : {
            filter,
            hasValidSignature: Boolean(response.data?.hasValidSignature ?? true),
            counts: {
              pending_with_you: Number(response.data?.counts?.pending_with_you || 0),
              requested_by_you: Number(response.data?.counts?.requested_by_you || 0),
            },
            letters: response.data?.letters || [],
          };

      if (requestId !== latestLoadRequestRef.current) return;

      setCounts(payload.counts);
      setHasValidSignature(Boolean(payload.hasValidSignature ?? true));
      setLetters(payload.letters || []);

      // Don't auto-open preview - just load the list with the correct filter selected
    } catch (err: any) {
      if (requestId !== latestLoadRequestRef.current) return;
      if (options?.signal?.aborted) return;
      if (options?.silent) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load pending approvals.');
      setHasValidSignature(true);
      setLetters([]);
    } finally {
      if (requestId !== latestLoadRequestRef.current) return;
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [openPreview]);

  // NOTE: no background polling. The queue is reloaded on mount, on filter change
  // and after an approve/reject action. An 8s silent refetch was the main cause of
  // the page visibly refreshing while the user was reviewing a letter.
  // On mount: pick filter from URL when provided, otherwise detect by openLetterId
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    const openLetterId = searchParams.get('openLetterId');

    if (filterParam === 'pending_with_you' || filterParam === 'requested_by_you') {
      if (filterParam !== activeFilter) {
        setActiveFilter(filterParam);
      }
      if (openLetterId) {
        const id = parseInt(openLetterId, 10);
        if (!Number.isNaN(id)) setPendingOpenLetterId(id);
      }
      return;
    }

    if (openLetterId) {
      const id = parseInt(openLetterId, 10);
      const allFilters: ApprovalFilterKey[] = ['pending_with_you', 'requested_by_you'];
      (async () => {
        for (const f of allFilters) {
          try {
            const res = await lettersApi.listPendingApproval({ filter: f });
            const letters: PendingLetterSummary[] = Array.isArray(res.data) ? res.data : (res.data?.letters || []);
            if (letters.some((l) => l.id === id)) {
              setActiveFilter(f);
              setPendingOpenLetterId(id);
              return;
            }
          } catch { /* continue */ }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadLetters(activeFilter, pendingOpenLetterId);
    if (pendingOpenLetterId) setPendingOpenLetterId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const handleApprove = async () => {
    if (!approveTarget?.id) return;

    try {
      setActionLoading(true);
      setActionMessage('');
      await lettersApi.approve(approveTarget.id);
      closeModals();
      setSelectedLetter(null);
      setActionMessage('✓ Letter approved successfully');
      await loadLetters(activeFilter, pendingOpenLetterId, { silent: true });
      window.setTimeout(() => setActionMessage(''), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to approve letter.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget?.id) return;
    const remark = rejectRemark.trim();
    if (!remark) {
      setRejectRemarkError('Rejection remark is required.');
      return;
    }

    try {
      setActionLoading(true);
      setActionMessage('');
      await lettersApi.reject(rejectTarget.id, { remark });
      closeModals();
      setSelectedLetter(null);
      setActionMessage('✓ Letter rejected successfully');
      await loadLetters(activeFilter, pendingOpenLetterId, { silent: true });
      window.setTimeout(() => setActionMessage(''), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reject letter.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecall = async (letter: PendingLetterSummary) => {
    const ok = window.confirm(
      `Recall "${letter.subject || letter.file_name || 'this letter'}" from approval?\n\nIt will be removed from the signatory's queue and open in the editor as a draft. After your changes, click "Submit for Signature" again.`,
    );
    if (!ok) return;
    try {
      setActionLoading(true);
      setError('');
      await lettersApi.recall(letter.id);
      navigateTo('letters-create', { letterId: String(letter.id) });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to recall letter.');
      await loadLetters(activeFilter, null, { silent: true });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <button onClick={() => navigateTo('letters-dashboard')} className="inline-flex items-center gap-2 text-violet-700 hover:text-violet-800 font-medium mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Letters Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Approval</h1>
          <p className="text-gray-600 mt-2">Review approval requests and rejected letters with remarks.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {actionMessage ? <div className="px-4 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 text-sm font-medium">{actionMessage}</div> : null}
        </div>
      </div>

      {error ? <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm font-medium">⚠ {error}</div> : null}
      {activeFilter === 'pending_with_you' && !hasValidSignature ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-amber-800 text-sm font-medium">
          ⚠ You need a saved signature before approving letters.
          <button
            type="button"
            onClick={() => navigateTo('letters-add-signature')}
            className="ml-2 underline font-semibold"
          >
            Add signature
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filterCards.map((card) => {
          const active = activeFilter === card.key;
          return (
            <button
              key={card.key}
              onClick={() => setActiveFilter(card.key)}
              className={`text-left rounded-2xl border transition-all px-5 py-4 bg-white shadow-sm ${active ? 'border-violet-400 ring-2 ring-violet-200' : 'border-gray-200 hover:border-violet-200'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-700">{card.title}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{card.count}</div>
                </div>
                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${card.tone} text-white flex items-center justify-center text-sm font-bold`}>
                  {card.count}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Approval Requests</h2>
            <span className="text-sm text-gray-500">{tableLetters.length} items</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading letters...</div>
          ) : tableLetters.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No approval requests found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Subject</th>
                    <th className="px-4 py-3 text-left font-semibold">File</th>
                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                    <th className="px-4 py-3 text-left font-semibold">Created By</th>
                    <th className="px-4 py-3 text-left font-semibold">Signatory</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Rejection Remark</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLetters.map((letter) => (
                    <tr key={letter.id} className="border-t border-gray-100 hover:bg-violet-50/40">
                      <td className="px-4 py-3 text-gray-900 font-medium">{letter.subject}</td>
                      <td className="px-4 py-3 text-gray-600">{letter.file_name || 'Untitled file'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDateTime(letter.letter_date)}</td>
                      <td className="px-4 py-3 text-gray-600">{letter.created_by_name || letter.created_by_email}</td>
                      <td className="px-4 py-3 text-gray-600">{letter.assigned_signatory_name || '-'}</td>
                      <td className="px-4 py-3">
                        {letter.status === 'pending_approval' ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Pending Approval</span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">Rejected</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-rose-700">{letter.rejection_remark || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openPreview(letter.id)}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {activeFilter === 'requested_by_you' && letter.status !== 'approved' ? (
                            <button
                              type="button"
                              onClick={() => navigateTo('letters-create', { letterId: String(letter.id) })}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          ) : null}
                          {activeFilter === 'requested_by_you' && letter.status === 'pending_approval' ? (
                            <button
                              type="button"
                              onClick={() => handleRecall(letter)}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs disabled:opacity-60"
                              title="Pull this letter back from approval, edit it and submit again"
                            >
                              <Undo2 className="w-3.5 h-3.5" /> Recall
                            </button>
                          ) : null}
                          {activeFilter === 'pending_with_you' && letter.status === 'pending_approval' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setApproveTarget(letter)}
                                disabled={actionLoading || !hasValidSignature}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-60"
                                title={!hasValidSignature ? 'Please add your signature first' : 'Approve'}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRejectTarget(letter);
                                  setRejectRemark('');
                                  setRejectRemarkError('');
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs disabled:opacity-60"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Reject
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Off-screen editor-accurate renderer for the review preview. Same engine
          as the draft editor; its generated Blob is shown in the modal iframe so
          the reviewer sees the true final layout (correct tables / signature).
          editable={false} so it never autosaves / writes localStorage. */}
      {previewRenderData && previewRenderExtract ? (
        <div style={OFFSCREEN_EDITOR_HOST_STYLE} aria-hidden="true">
          <NewLetterEditor
            key={`pending-preview-${previewRenderData.id}`}
            editable={false}
            content={previewRenderExtract.cleanHtml}
            onChange={noopEditorChange}
            title={previewRenderData.subject || previewRenderData.file_name || `letter-${previewRenderData.id}`}
            qrCodeData={previewRenderData.qr_code_data || null}
            letterNumber={previewRenderData.letter_number || null}
            letterDate={previewRenderData.letter_date || null}
            approvedSignatureHtml={previewRenderData.approved_signature_html || null}
            signaturePlacementConfig={previewRenderExtract.config}
            registerPdfDownloader={(fn) => { previewPdfDownloaderRef.current = fn; }}
          />
        </div>
      ) : null}

      {selectedLetter ? (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Letter Preview</h3>

            {accessPopupMessage ? (
              <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-amber-900">Access denied</h3>
                    <button type="button" onClick={() => setAccessPopupMessage('')} className="text-amber-700 hover:text-amber-900">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-gray-700">{accessPopupMessage}</p>
                  </div>
                  <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setAccessPopupMessage('')}
                      className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
                <p className="text-xs text-gray-500">{selectedLetter.subject}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLetter(null)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto bg-gray-50 flex-1">
              {detailLoading ? (
                <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">Loading letter preview...</div>
              ) : previewPdfFailed ? (
                // Editor-accurate preview failed — fall back to the legacy
                // renderer so the preview is never blank.
                <LetterDocument
                  subject={selectedLetter.subject}
                  letterDate={selectedLetter.letter_date}
                  serialNumber={selectedLetter.serial_number}
                  letterNumber={selectedLetter.letter_number}
                  bodyHtml={selectedLetter.body_html}
                  letterheadHtml={selectedLetter.letterhead_html}
                  footerHtml={selectedLetter.footer_html}
                  approvedSignatureHtml={selectedLetter.approved_signature_html}
                  qrCodeData={selectedLetter.qr_code_data}
                />
              ) : previewPdfUrl ? (
                // Editor-accurate render shown as a PDF — identical to download.
                <div className="bg-white border border-gray-200 rounded-xl p-0 overflow-hidden h-full">
                  <iframe
                    src={previewPdfUrl}
                    className="w-full h-full"
                    style={{ minHeight: '70vh' }}
                    title={`Letter Preview: ${selectedLetter.subject || selectedLetter.letter_number || ''}`}
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2 align-[-2px]" />
                  Rendering letter preview...
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {approveTarget ? (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Confirm Approval</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to approve <span className="font-semibold text-gray-900">{approveTarget.subject}</span>?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApprove}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                disabled={actionLoading}
              >
                {actionLoading ? 'Approving...' : 'Confirm Approve'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectTarget ? (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-xl p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Reject Letter</h3>
            <p className="text-sm text-gray-600">
              Add mandatory remark for rejection of <span className="font-semibold text-gray-900">{rejectTarget.subject}</span>.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remark *</label>
              <textarea
                value={rejectRemark}
                onChange={(e) => {
                  setRejectRemark(e.target.value);
                  if (rejectRemarkError) setRejectRemarkError('');
                }}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                placeholder="Please provide reason for rejection..."
              />
              {rejectRemarkError ? <p className="text-xs text-rose-600 mt-1">{rejectRemarkError}</p> : null}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                disabled={actionLoading}
              >
                {actionLoading ? 'Rejecting...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
